/**
 * Sélecteur d'EXERCICE en modale (remplace la liste déroulante) : recherche
 * (titre, chapitre — sans accents), filtre par niveau en puces, ligne avec
 * illustration, niveau, chapitre et difficulté. Utilisé pour assigner un
 * exercice (classe, élève), choisir l'exercice d'un défi Live, relier une leçon.
 */

import { useState } from 'react';
import { BookOpen, Check } from 'lucide-react';
import { MathText } from '@jeraisonne/ui';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import { stripMath } from '@jeraisonne/db/cards';
import { PickerField, PickerModal, PickerOption, type PickerChip } from './picker-modal';
import { matchesQuery } from '@/lib/contact-search';

export interface ExercisePickerItem {
  id: string;
  title: string;
  level?: string | null;
  chapterTitle?: string | null;
  difficulty?: 'easy' | 'medium' | 'hard' | null;
  illustrationUrl?: string | null;
}

/** Résumé d'exercice de la bibliothèque tuteur (`MyExerciseSummary`) → élément du sélecteur. */
export function toExercisePickerItem(e: {
  id: string;
  title: string;
  level?: string | null;
  chapter_title?: string | null;
  difficulty?: 'easy' | 'medium' | 'hard' | null;
  illustration_url?: string | null;
}): ExercisePickerItem {
  return {
    id: e.id,
    title: e.title,
    level: e.level ?? null,
    chapterTitle: e.chapter_title ?? null,
    difficulty: e.difficulty ?? null,
    illustrationUrl: e.illustration_url ?? null,
  };
}

const DIFFICULTY_LABEL = { easy: 'Facile', medium: 'Moyen', hard: 'Difficile' } as const;
const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

/** Filtre pur (recherche sans accents sur titre + chapitre, niveau exact). */
export function filterExercisePickerItems(
  items: readonly ExercisePickerItem[],
  query: string,
  level: string,
): ExercisePickerItem[] {
  return items.filter(
    (e) => (!level || e.level === level) && matchesQuery(query, stripMath(e.title), e.chapterTitle),
  );
}

/** Puces de niveau présentes dans la liste (ordre scolaire, avec effectifs). */
export function exerciseLevelChips(items: readonly ExercisePickerItem[]): PickerChip[] {
  return SCHOOL_LEVELS.map((l) => ({
    value: l.value as string,
    label: l.label,
    count: items.filter((e) => e.level === l.value).length,
  })).filter((c) => c.count > 0);
}

function meta(e: ExercisePickerItem): string {
  return [
    e.level ? (LEVEL_LABEL[e.level] ?? e.level) : null,
    e.chapterTitle || null,
    e.difficulty ? DIFFICULTY_LABEL[e.difficulty] : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function ExerciseLine({ item }: { item: ExercisePickerItem }) {
  const sub = meta(item);
  return (
    <>
      {item.illustrationUrl ? (
        <img
          src={item.illustrationUrl}
          alt=""
          loading="lazy"
          className="bg-ink-100 h-11 w-11 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <span className="bg-primary-50 text-primary-600 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
          <BookOpen className="h-5 w-5" aria-hidden />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="text-ink-900 line-clamp-2 block text-sm font-semibold">
          <MathText>{item.title}</MathText>
        </span>
        {sub && <span className="text-ink-500 block truncate text-xs">{sub}</span>}
      </span>
    </>
  );
}

export function ExercisePickerField({
  items,
  value,
  onChange,
  label,
  placeholder = 'Choisir un exercice…',
  title = 'Choisir un exercice',
  disabled,
}: {
  items: readonly ExercisePickerItem[];
  value: string | null;
  onChange: (id: string) => void;
  label?: string;
  placeholder?: string;
  title?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [level, setLevel] = useState('');
  const selected = items.find((e) => e.id === value) ?? null;
  const shown = filterExercisePickerItems(items, query, level);

  return (
    <>
      <PickerField
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        onOpen={() => setOpen(true)}
        selected={
          selected ? (
            <span className="flex min-w-0 items-center gap-3">
              <ExerciseLine item={selected} />
            </span>
          ) : null
        }
      />
      {open && (
        <PickerModal
          title={title}
          query={query}
          onQuery={setQuery}
          searchPlaceholder="Rechercher un exercice (titre, chapitre)…"
          chips={exerciseLevelChips(items)}
          chipValue={level}
          onChip={setLevel}
          allChipsLabel="Tous les niveaux"
          onClose={() => setOpen(false)}
        >
          {shown.length === 0 ? (
            <p className="text-ink-500 px-3 py-8 text-center text-sm">
              {items.length === 0 ? 'Aucun exercice disponible.' : 'Aucun exercice ne correspond.'}
            </p>
          ) : (
            <div role="listbox" aria-label={title} className="space-y-1">
              {shown.map((e) => (
                <PickerOption
                  key={e.id}
                  label={stripMath(e.title)}
                  selected={e.id === value}
                  onSelect={() => {
                    onChange(e.id);
                    setOpen(false);
                  }}
                >
                  <ExerciseLine item={e} />
                  {e.id === value && (
                    <Check className="text-primary-600 h-5 w-5 shrink-0" aria-hidden />
                  )}
                </PickerOption>
              ))}
            </div>
          )}
        </PickerModal>
      )}
    </>
  );
}
